import moment from 'moment';

import {getProjectId} from 'common/report/util/index';
import {apiClient} from 'common/util/api-client';
import {VALID_TIME_FORMAT} from 'common/report/util/profile/utils';

import {appApiUrl} from '../utils';

export interface SetPropertiesParams {
  profileId: string;
  properties: {[name: string]: string};
}

type BatchProfileResponse = {
  updated_profiles?: string[];
};
/**
 * The profile property client is a thin, dumb wrapper around `fetch`.
 * It is used to create, update, and delete profile properties for a given or a batch of profile, *using that project's key*.
 */
class ProfilePropertyClient {
  private getBasePayload(profileId: string, dataGroupKey): any {
    let data: any = {};

    if (dataGroupKey) {
      data = {
        $group_id: profileId,
        $group_key: dataGroupKey,
      };
    } else {
      data = {
        $distinct_id: profileId,
      };
    }
    return data;
  }

  deleteProfileProperties(
    {profileId, propertyNames}: {profileId: string; propertyNames: string[]},
    dataGroupKey: string,
  ): Promise<void> {
    let data = this.getBasePayload(profileId, dataGroupKey);

    data = {
      ...data,
      $ignore_time: true,
      $ip: 0,
      $unset: propertyNames,
    };
    return this._post(data);
  }

  deleteProfile(profileId: string, dataGroupKey: string): Promise<void> {
    let data = this.getBasePayload(profileId, dataGroupKey);

    data = {
      ...data,
      $delete: true,
    };
    // b2b doesn't support id merge, causes API rejection
    if (!dataGroupKey) {
      data.$ignore_alias = true;
    }

    return this._post(data);
  }

  async batchDeleteProfiles(
    profileIds: string[],
    dataGroupKey: string,
  ): Promise<BatchProfileResponse & {error?: string}> {
    const data = profileIds.map((id) => {
      const profilePayload = {...this.getBasePayload(id, dataGroupKey), $delete: true};
      // b2b doesn't support id merge, causes API rejection
      if (!dataGroupKey) {
        profilePayload.$ignore_alias = true;
      }
      return profilePayload;
    });
    return this._post({profiles: data}, true);
  }

  formatTimeValueProperties(properties, utcOffset) {
    const propertiesWithFormattedTimeValues = {...properties};
    Object.keys(propertiesWithFormattedTimeValues).forEach((propKey) => {
      const value = propertiesWithFormattedTimeValues[propKey];
      if (moment(value, VALID_TIME_FORMAT, true).isValid()) {
        const currentTime = moment(value, VALID_TIME_FORMAT, true);
        const utcTime = currentTime.subtract(utcOffset, `minutes`);
        propertiesWithFormattedTimeValues[propKey] = utcTime.format(VALID_TIME_FORMAT);
      }
    });
    return propertiesWithFormattedTimeValues;
  }
  //bulk create or update profiles
  async batchSetProperties(
    profilesParam: SetPropertiesParams[],
    dataGroupKey: string,
    utcOffset: number,
  ): Promise<BatchProfileResponse & {error?: string}> {
    const data = profilesParam.map((profile) => {
      const properties = this.formatTimeValueProperties(profile.properties, utcOffset);
      return {...this.getBasePayload(profile.profileId, dataGroupKey), $set: properties, $ignore_time: true, $ip: 0};
    });
    return this._post({profiles: data}, true);
  }
  //create or update profile properties
  async setProperties(profileParams: SetPropertiesParams, dataGroupKey: string, utcOffset: number): Promise<void> {
    let data = this.getBasePayload(profileParams.profileId, dataGroupKey);

    const properties = this.formatTimeValueProperties(profileParams.properties, utcOffset);

    data = {
      $ignore_time: true,
      $ip: 0,
      $set: properties,
      ...data,
    };

    return this._post(data);
  }

  private _post(data: any, isBatch = false): Promise<any> {
    const projectId = getProjectId();
    const url = appApiUrl(isBatch ? `batch-profile-update` : `profile-update`, {projectId});
    return apiClient.post(url, data);
  }
}

export const profilePropertyClient = new ProfilePropertyClient();
